import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import AsyncSelect from 'react-select/async';
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { PAYMENT_METHODS } from "../../../utils/paymentMethods";
import { getSelectStyles } from "../../../utils/selectStyles";
import {
  ServiceSelect,
  getServiceVendor,
  getServiceTiers,
  indexRates,
  rateForCredits,
  describeTier,
} from "../../../utils/resellerServices";
import "../../accounts/create/CreateAccountPage.css";

export default function CreateResellerCreditsPage() {
  const [searchParams] = useSearchParams();

  const [resellers, setResellers] = useState([]);
  const [rates, setRates]         = useState({});
  const [created, setCreated]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);
  const [formError, setFormError] = useState(null);

  const [purchase, setPurchase] = useState({
    accountID: "", service: "", credits: "", amountPaid: "",
    transactionDate: null, paymentMethod: "", notes: "",
  });

  // Our cost per credit. Null means "follow the rate card"; once the user types
  // here their figure sticks, even if the quantity crosses a volume threshold.
  const [costRateOverride, setCostRateOverride] = useState(null);
  const [resellerOption, setResellerOption]     = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [accRes, rateRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/reseller-rates`, { headers }),
        ]);

        const onlyResellers = (accRes.data || []).filter(a => a.accountType === 'reseller');
        setResellers(onlyResellers);
        setRates(indexRates(rateRes.data));

        // Arriving from a reseller's row pre-selects them.
        const preset = searchParams.get('accountId');
        const match = preset && onlyResellers.find(a => a._id === preset);
        if (match) {
          setPurchase(prev => ({ ...prev, accountID: match._id }));
          setResellerOption({ value: match._id, label: match.username });
        }

        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.error || err.response?.data?.message || "Failed to load resellers");
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'transactionDate') {
      setPurchase(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setPurchase(prev => ({ ...prev, [name]: value }));
    }
    setCreated(false);
  };

  const loadOptions = (inputValue, callback) => {
    const filtered = resellers.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const formatDateForInput = (unixMs) =>
    unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  /* ── Live figures ── */
  const credits    = Number(purchase.credits) || 0;
  const amountPaid = Number(purchase.amountPaid) || 0;
  const rateDoc    = rates[purchase.service];

  const cardRate = useMemo(
    () => (purchase.service ? rateForCredits(rateDoc, credits) : null),
    [rateDoc, credits, purchase.service]
  );

  // An emptied override is "no cost entered", not zero — treating it as 0 would
  // silently report the whole payment as profit.
  const overrideRate = costRateOverride !== null && String(costRateOverride).trim() !== ""
    ? Number(costRateOverride)
    : null;
  const costRate   = costRateOverride !== null ? overrideRate : cardRate;
  const costEUR    = costRate != null && credits > 0 ? credits * costRate : null;
  const creditRate = credits > 0 ? amountPaid / credits : null;
  const profit     = costEUR != null ? amountPaid - costEUR : null;
  const marginPct  = profit != null && amountPaid > 0 ? (profit / amountPaid) * 100 : null;

  const ladderHint = purchase.service
    ? (describeTier(rateDoc, credits) ?? 'no rate card for this service yet')
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setCreated(false);

    if (!purchase.accountID)            return setFormError("Please select a reseller.");
    if (!resellers.some(a => a._id === purchase.accountID))
      return setFormError("That account is not flagged as a reseller.");
    if (!purchase.service)              return setFormError("Please select a service.");
    if (credits <= 0)                   return setFormError("Enter how many credits they bought.");
    if (amountPaid < 0)                 return setFormError("Amount paid cannot be negative.");
    if (costRate == null || !Number.isFinite(costRate) || costRate < 0 || costEUR == null)
      return setFormError("Enter our cost per credit, or set a rate card for this service.");
    if (!purchase.transactionDate)      return setFormError("Please select a date.");

    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/transaction/create`,
        {
          accountID: purchase.accountID,
          type: 'reseller-credits',
          revenue: amountPaid,
          profit: Number(profit.toFixed(2)),
          notes: purchase.notes,
          transactionDate: purchase.transactionDate + "",
          paymentMethod: purchase.paymentMethod || null,
          resellerCredits: {
            service: purchase.service,
            vendor: getServiceVendor(purchase.service),
            credits,
            creditRate: creditRate != null ? Number(creditRate.toFixed(4)) : null,
            costRate,
            costEUR: Number(costEUR.toFixed(2)),
            costSource: costRateOverride !== null ? 'override' : 'rate-card',
          },
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCreated(true);
      setPurchase({
        accountID: "", service: "", credits: "", amountPaid: "",
        transactionDate: null, paymentMethod: "", notes: "",
      });
      setCostRateOverride(null);
      setResellerOption(null);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to log credit purchase");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const serviceTiers = purchase.service ? getServiceTiers(purchase.service) : [];

  return (
    <>
      <Sidebar title="Log Credit Purchase" />
      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div className="form-panel-icon"><i className="bi bi-coin" /></div>
            <div>
              <div className="form-panel-title">Log a Reseller Credit Purchase</div>
              <div className="form-panel-subtitle">Credits sold to a reseller, with profit worked out from your cost rate</div>
            </div>
          </div>

          <div className="form-panel-body">
            {resellers.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--color-subtle)', fontSize: '0.9rem' }}>
                <i className="bi bi-person-badge" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />
                No reseller accounts yet — create an account and tick "This is a reseller account" first.
              </div>
            ) : (
            <form onSubmit={handleSubmit} noValidate>

              <div className="form-section-label">Reseller &amp; Service</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-badge-fill" />Reseller <span className="required-star">*</span></label>
                    <AsyncSelect
                      cacheOptions defaultOptions
                      loadOptions={loadOptions}
                      value={resellerOption}
                      onChange={opt => { setResellerOption(opt); setPurchase(prev => ({ ...prev, accountID: opt?.value || "" })); }}
                      placeholder="Search resellers…"
                      isClearable
                      styles={getSelectStyles()}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-hdd-stack" />Service <span className="required-star">*</span></label>
                    <select className="form-input" name="service" value={purchase.service} onChange={handleChange} required>
                      <ServiceSelect />
                    </select>
                    {serviceTiers.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--color-subtle)' }}>
                        {serviceTiers.map(t => `${t.brand}: ${t.tier}`).join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Credits &amp; Payment</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-coin" />Credits Bought <span className="required-star">*</span></label>
                    <input className="form-input" type="number" name="credits" value={purchase.credits}
                      onChange={handleChange} step="1" min="1" placeholder="e.g. 80" required />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-cash-stack" />Amount Paid <span className="required-star">*</span></label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="amountPaid" value={purchase.amountPaid}
                        onChange={handleChange} step="0.01" min="0" placeholder="0.00" required />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-event" />Date <span className="required-star">*</span></label>
                    <input className="form-input" type="date" name="transactionDate"
                      value={formatDateForInput(purchase.transactionDate)} onChange={handleChange} required />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-credit-card" />Payment Method</label>
                    <select className="form-input" name="paymentMethod" value={purchase.paymentMethod} onChange={handleChange}>
                      <option value="">Select method…</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Our Cost</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-tags" />Our € per credit <span className="required-star">*</span></label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input
                        className="form-input" type="number" step="0.01" min="0"
                        value={costRateOverride !== null ? costRateOverride : (cardRate ?? "")}
                        onChange={e => setCostRateOverride(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--color-subtle)', display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span>
                        {costRateOverride !== null
                          ? 'Overridden for this purchase'
                          : (ladderHint ?? 'pick a service to load the rate card')}
                      </span>
                      {costRateOverride !== null && (
                        <button type="button" onClick={() => setCostRateOverride(null)}
                          style={{ background: 'none', border: 'none', padding: 0, color: '#60a5fa', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}>
                          use rate card
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(6,182,212,0.06)', border: '1px solid rgba(6,182,212,0.2)', fontSize: '0.82rem' }}>
                    <div style={{ color: 'var(--color-subtle)', marginBottom: 8 }}>
                      {credits > 0 ? `${credits} credits` : '— credits'}
                      {purchase.service ? ` · Service ${purchase.service} (${getServiceVendor(purchase.service)})` : ''}
                    </div>
                    <SummaryRow label="Reseller pays" value={`€${amountPaid.toFixed(2)}`}
                      hint={creditRate != null ? `€${creditRate.toFixed(2)} / credit` : null} color="#34d399" />
                    <SummaryRow label="Our cost" value={costEUR != null ? `€${costEUR.toFixed(2)}` : '—'}
                      hint={costRate != null ? `€${Number(costRate).toFixed(2)} / credit` : null} color="#f87171" />
                    <SummaryRow label="Profit" value={profit != null ? `€${profit.toFixed(2)}` : '—'}
                      hint={marginPct != null ? `${marginPct.toFixed(1)}% margin` : null} color="#38bdf8" strong />
                  </div>
                </div>
              </div>

              <div className="row g-4">
                <div className="col-12">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-journal-text" />Notes</label>
                    <textarea className="form-input" name="notes" value={purchase.notes || ''}
                      onChange={handleChange} rows={3} placeholder="Additional notes…"
                      style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {created   && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Credit purchase logged successfully!</div>}
              {formError && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{formError}</div>}
              {error     && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button type="submit" className="btn-form-submit" disabled={submitting}>
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-plus-circle'}`} />
                  {submitting ? 'Logging…' : 'Log Purchase'}
                </button>
              </div>

            </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ── One line of the live summary ──────────────────────────────────── */
function SummaryRow({ label, value, hint, color, strong }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '3px 0' }}>
      <span style={{ color: 'var(--color-muted)' }}>{label}</span>
      <span>
        <span style={{ color, fontWeight: strong ? 700 : 600 }}>{value}</span>
        {hint && <span style={{ color: 'var(--color-subtle)', marginLeft: 8, fontSize: '0.75rem' }}>({hint})</span>}
      </span>
    </div>
  );
}
